import { useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link, useNavigate, useParams } from "@tanstack/react-router"
import {
	IconFingerprintOutline18 as Fingerprint,
	IconSquareArrowUpRightOutline18 as OpenIcon,
	IconTrash2Outline18 as Trash2,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import type { PortalLandingSection } from "@productlane/lib/src/portal-landing"
import type { PortalInstance } from "@productlane/zero"
import type { UpdatePortalInstanceArgs } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/browser"
import { enabledAddresses } from "@productlane/lib/src/email-settings"
import {
	AlertDialog,
	Badge,
	Button,
	Form,
	FormInput,
	Loader,
	Select,
	Switch,
	Tabs,
	TabsList,
	TabsTrigger,
	useZodForm,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { captureError } from "@/lib/capture-error"
import { useAutosaveText } from "@/lib/use-autosave-text"
import { useZero, useZeroWorkspace } from "@/lib/zero"
import { useSyncedTeams } from "@/screens/app/threads/hooks/useSelectedTeams"
import {
	addressIdToSelectValue,
	portalHostname,
	resolveAddressId,
	resolveTeamId,
	showInSelectorToVisibility,
	slugify,
	SWITCHER_OPTIONS,
	teamIdToSelectValue,
	themeOverrideArgs,
	validateSlug,
	visibilityToShowInSelector,
	WORKSPACE_DEFAULT_TEAM,
} from "@/screens/settings/portal-instance-form"
import { PortalLogoField } from "@/screens/settings/portal-logo-field"
import {
	endpointHost,
	SSO_LIST_PATH,
} from "@/screens/settings/portal-sso/sso-targets"
import { SettingsHeader } from "@/screens/settings/settings-header"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "@/screens/settings/settings-pages/settings-nav-row"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { usePortalLogoUpload } from "@/screens/settings/use-portal-logo-upload"
import { PortalContentCard } from "./portal-content-card"
import {
	PortalDomainBody,
	RemovePortalDomainDialog,
} from "./portal-domain-section"
import { PortalLandingSelect } from "./portal-landing-select"
import {
	PortalAuthSection,
	PortalLinksSection,
} from "./portal-override-sections"
import { PortalThemeSection } from "./portal-theme-section"

const LIST_PATH = "/settings/portal/instances"

const FormSchema = z.object({
	name: z.string().min(1).max(80),
	slug: z.string(),
})

export function PortalDetailPage() {
	const { portalId } = useParams({ strict: false })
	const workspace = useZeroWorkspace()

	const instance = useMemo(
		() => (workspace?.portalInstances ?? []).find((i) => i.id === portalId),
		[workspace?.portalInstances, portalId],
	)

	if (!workspace) {
		return (
			<>
				<SettingsHeader title="Portal" />
				<SettingsContainer>
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				</SettingsContainer>
			</>
		)
	}

	if (!instance) {
		return (
			<>
				<MetaInfo title="Portal" />
				<SettingsHeader
					title="Portal not found"
					subtitle="It was deleted, or the link points at another workspace."
				/>
				<Link
					to={LIST_PATH}
					className="text-sm font-medium text-secondary hover:text-primary"
				>
					Back to Portals
				</Link>
			</>
		)
	}

	return (
		<PortalDetail
			key={instance.id}
			instance={instance}
			hostname={portalHostname(workspace)}
			workspaceId={workspace.id}
			otherSlugs={(workspace.portalInstances ?? [])
				.filter((i) => i.id !== instance.id)
				.map((i) => i.slug)}
			addresses={enabledAddresses(workspace.emailSettings)}
			workspaceTheme={workspace}
			workspaceSections={{
				docs: workspace.helpCenterPublic ?? false,
				changelog: workspace.changelogPublic ?? false,
				roadmap: workspace.domainPublic ?? false,
				support: workspace.customerPortalSettings?.isEnabled ?? false,
			}}
		/>
	)
}

type AddressMode = "path" | "domain"

function PortalDetail({
	instance,
	hostname,
	workspaceId,
	otherSlugs,
	addresses,
	workspaceTheme,
	workspaceSections,
}: {
	instance: PortalInstance
	hostname: string
	workspaceId: string
	otherSlugs: string[]
	addresses: { id: string; primaryEmail: string }[]
	workspaceTheme: Parameters<typeof themeOverrideArgs>[1]
	workspaceSections: Record<PortalLandingSection, boolean>
}) {
	const zero = useZero()
	const navigate = useNavigate()
	const uploadLogo = usePortalLogoUpload()
	const teams = useSyncedTeams()

	const [logoPreview, setLogoPreview] = useState<string | null>(null)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [confirmingDomainRemoval, setConfirmingDomainRemoval] = useState(false)
	const [addressMode, setAddressMode] = useState<AddressMode>(
		instance.customDomain ? "domain" : "path",
	)
	useEffect(() => {
		setAddressMode(instance.customDomain ? "domain" : "path")
	}, [instance.customDomain])
	const [portalSso] = useQuery(queries.portalInstanceSso())
	const sso = portalSso.find((i) => i.id === instance.id)?.ssoSettings ?? null

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: { name: instance.name, slug: instance.slug },
	})

	const save = async (args: Omit<UpdatePortalInstanceArgs, "id">) => {
		try {
			const mutation = zero.mutate(
				mutators.portalInstances.update({ id: instance.id, ...args }),
			)
			await mutation.client
			mutation.server.catch((err) => {
				captureError(err, {
					domain: "portal",
					action: "update portal instance",
					workspaceId,
					extra: { portalInstanceId: instance.id },
				})
				toast.error({
					title: "Your change did not save",
					description: err instanceof Error ? err.message : "Try again.",
				})
			})
		} catch (e) {
			captureError(e, {
				domain: "portal",
				action: "update portal instance",
				workspaceId,
				extra: { portalInstanceId: instance.id },
			})
			toast.error({
				title: "Your change did not save",
				description: e instanceof Error ? e.message : "Try again.",
			})
		}
	}

	const nameWatch = form.watch("name")
	useAutosaveText({
		value: nameWatch,
		persistedValue: instance.name,
		onSave: (name) => save({ name }),
		enabled: !!form.formState.dirtyFields.name && nameWatch.trim().length > 0,
	})

	const saveSlug = (raw: string) => {
		const slug = slugify(raw)
		if (slug === instance.slug) return
		const message = validateSlug(slug)
		if (message) {
			form.setError("slug", { message })
			return
		}
		if (otherSlugs.includes(slug)) {
			form.setError("slug", {
				message: `A portal already uses /${slug}. Pick another URL.`,
			})
			return
		}
		form.clearErrors("slug")
		form.setValue("slug", slug)
		void save({ slug })
	}

	const handleDelete = async () => {
		try {
			const mutation = zero.mutate(
				mutators.portalInstances.delete({ id: instance.id }),
			)
			await mutation.client
			void navigate({ href: LIST_PATH, replace: true })
			mutation.server.catch((err) => {
				captureError(err, {
					domain: "portal",
					action: "delete portal instance",
					workspaceId,
					extra: { portalInstanceId: instance.id },
				})
				toast.error({
					title: "Your portal was not deleted",
					description: err instanceof Error ? err.message : "Try again.",
				})
			})
		} catch (e) {
			captureError(e, {
				domain: "portal",
				action: "delete portal instance",
				workspaceId,
				extra: { portalInstanceId: instance.id },
			})
			toast.error({
				title: "Your portal was not deleted",
				description: e instanceof Error ? e.message : "Try again.",
			})
		}
	}

	const workspaceUrl = `${hostname}/${instance.slug}`
	const portalUrl = instance.customDomain
		? `https://${instance.customDomain}`
		: `https://${workspaceUrl}`

	return (
		<>
			<MetaInfo title={instance.name} />

			<SettingsHeader
				title={instance.name}
				subtitle={instance.customDomain ?? workspaceUrl}
				titleSuffix={
					!instance.showInSelector ? (
						<Badge variant="dark-gray">No switcher</Badge>
					) : null
				}
				action={
					<Button
						variant="outline"
						Icon={OpenIcon}
						onClick={() => window.open(portalUrl, "_blank")}
					>
						Open portal
					</Button>
				}
			/>

			<Form form={form} onSubmit={() => undefined}>
				<SettingsContainer className="flex flex-col gap-4 divide-y-0">
					<SettingItem
						title="Logo"
						description="Shown in the portal header instead of the workspace logo."
						action={
							<PortalLogoField
								size="md"
								logoUrl={logoPreview ?? instance.logoUrl ?? null}
								onPicked={(file, previewUrl) => {
									setLogoPreview(previewUrl)
									void uploadLogo(file)
										.then((fileUrl) => save({ logoUrl: fileUrl }))
										.catch((e: unknown) => {
											setLogoPreview(null)
											toast.error({
												title: "Your logo did not upload",
												description:
													e instanceof Error
														? e.message
														: "Try uploading it again.",
											})
										})
								}}
								onCleared={() => {
									setLogoPreview(null)
									void save({ logoUrl: null })
								}}
							/>
						}
					/>

					<SettingItem
						title="Name"
						description="Shown in the portal switcher and in the browser tab."
						action={
							<FormInput
								name="name"
								className="max-w-60"
								onBlur={(e) => {
									const value = e.target.value.trim()
									if (form.formState.dirtyFields.name && value.length > 0) {
										void save({ name: value })
									}
								}}
							/>
						}
					/>
				</SettingsContainer>
			</Form>

			<SettingsContainer className="mt-3 flex flex-col gap-3 divide-y-0">
				<SettingItem
					title="Address"
					description="Where visitors open this portal."
					action={
						<Tabs
							value={addressMode}
							onValueChange={(value) => {
								if (value === "path" && instance.customDomain) {
									setConfirmingDomainRemoval(true)
									return
								}
								setAddressMode(value as AddressMode)
							}}
						>
							<TabsList>
								<TabsTrigger value="path">Path</TabsTrigger>
								<TabsTrigger value="domain">Own domain</TabsTrigger>
							</TabsList>
						</Tabs>
					}
				/>
				{addressMode === "path" ? (
					<Form form={form} onSubmit={() => undefined}>
						<FormInput
							name="slug"
							errorMessage={form.formState.errors.slug?.message}
							prefix={<span className="text-sm">{hostname}/</span>}
							onBlur={(e) => saveSlug(e.target.value)}
						/>
						<div className="mt-1.5 text-xs text-tertiary">
							Old links stop working when you change the path.
						</div>
					</Form>
				) : (
					<PortalDomainBody
						portalInstanceId={instance.id}
						customDomain={instance.customDomain ?? null}
						onRemove={() => setConfirmingDomainRemoval(true)}
					/>
				)}
			</SettingsContainer>
			{instance.customDomain ? (
				<RemovePortalDomainDialog
					portalInstanceId={instance.id}
					customDomain={instance.customDomain}
					fallbackUrl={workspaceUrl}
					isOpen={confirmingDomainRemoval}
					onOpenChange={setConfirmingDomainRemoval}
				/>
			) : null}

			<SettingsSection
				title="Sections"
				description="Pick what this portal shows."
				className="mb-3"
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				<SectionSwitch
					title="Docs"
					description="Help center articles assigned to this portal."
					checked={instance.showDocs}
					onChange={(showDocs) => void save({ showDocs })}
				/>
				<SectionSwitch
					title="Changelog"
					description="Entries assigned to this portal."
					checked={instance.showChangelog}
					onChange={(showChangelog) => void save({ showChangelog })}
				/>
				<SectionSwitch
					title="Feature requests"
					description="The public roadmap and the request form."
					checked={instance.showRoadmap}
					onChange={(showRoadmap) => void save({ showRoadmap })}
				/>
				<SectionSwitch
					title="Support portal"
					description="Signed-in customers see their own threads."
					checked={instance.showSupport}
					onChange={(showSupport) => void save({ showSupport })}
				/>
			</SettingsContainer>

			<PortalAuthSection
				values={instance}
				onSave={(args) => void save(args)}
				gate={{
					checked: instance.overridesAuth,
					onChange: (overridesAuth) => void save({ overridesAuth }),
				}}
			/>

			<div className={cn(settingsNavGroupClass, "mb-8 mt-4")}>
				<SettingsNavRow
					icon={Fingerprint}
					title="SSO"
					description={
						sso
							? (endpointHost(sso.tokenEndpoint) ?? "Own identity provider")
							: "Uses the root portal's connection"
					}
					status={sso ? sso.isEnabled : undefined}
					link={`${SSO_LIST_PATH}/${instance.id}`}
				/>
			</div>

			<PortalLinksSection
				values={instance}
				onSave={(args) => void save(args)}
				gate={{
					checked: instance.overridesCustomLinks,
					onChange: (overridesCustomLinks) =>
						void save({ overridesCustomLinks }),
				}}
			/>

			<PortalThemeSection
				values={{
					darkMode: instance.darkMode ?? null,
					accentColor: instance.accentColor ?? null,
					buttonTextColor: instance.buttonTextColor ?? null,
					darkModeAccentColor: instance.darkModeAccentColor ?? null,
					darkModeButtonTextColor: instance.darkModeButtonTextColor ?? null,
					backgroundColor: instance.backgroundColor ?? null,
					darkModeBackgroundColor: instance.darkModeBackgroundColor ?? null,
					portalEmbedImageUrl: instance.portalEmbedImageUrl ?? null,
				}}
				fallback={workspaceTheme}
				onSave={save}
				gate={{
					checked: instance.overridesTheme,
					onChange: (overridesTheme) =>
						void save(
							overridesTheme
								? themeOverrideArgs(instance, workspaceTheme)
								: { overridesTheme },
						),
				}}
			/>

			<SettingsSection title="Routing" className="mb-3" />
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				<SettingItem
					title="Default portal"
					description={
						instance.isDefault
							? `${hostname} sends visitors here, and the main portal moved to ${hostname}/main.`
							: `Send visitors to ${hostname} here instead of to the main portal.`
					}
					action={
						<Switch
							name="isDefault"
							checked={instance.isDefault}
							onCheckedChange={(isDefault) => void save({ isDefault })}
						/>
					}
				/>
				<SettingItem
					title="Portal switcher"
					description="Visitors on this portal can switch to your other portals."
					action={
						<Select
							name="visibility"
							className="max-w-60"
							selectClassName="dark:bg-gray-700/70"
							value={showInSelectorToVisibility(instance.showInSelector)}
							onChange={(value) =>
								void save({
									showInSelector: visibilityToShowInSelector(
										value === "hidden" ? "hidden" : "shown",
									),
								})
							}
							options={SWITCHER_OPTIONS.map(({ value, label, Icon }) => ({
								value,
								label: (
									<span className="flex items-center gap-2">
										<Icon className="size-4 flex-none text-tertiary" />
										{label}
									</span>
								),
							}))}
						/>
					}
				/>
				<PortalLandingSelect
					value={instance.landingSection}
					portalInstanceId={instance.id}
					sections={{
						docs: workspaceSections.docs && instance.showDocs,
						changelog: workspaceSections.changelog && instance.showChangelog,
						roadmap: workspaceSections.roadmap && instance.showRoadmap,
						support: workspaceSections.support && instance.showSupport,
					}}
					onChange={(landingSection) => void save({ landingSection })}
				/>
				{teams.length ? (
					<SettingItem
						title="Linear team"
						description="Threads that start on this portal land on this team."
						action={
							<Select
								name="defaultLinearTeamId"
								className="max-w-60"
								selectClassName="dark:bg-gray-700/70"
								value={teamIdToSelectValue(instance.defaultLinearTeamId)}
								onChange={(value) =>
									void save({ defaultLinearTeamId: resolveTeamId(value) })
								}
								options={[
									{ label: "Workspace default", value: WORKSPACE_DEFAULT_TEAM },
									...teams.map((team) => ({
										label: team.name,
										value: team.id,
									})),
								]}
							/>
						}
					/>
				) : null}
				{addresses.length > 1 ? (
					<SettingItem
						title="Reply address"
						description="Customers on this portal hear back from this address."
						action={
							<Select
								name="defaultEmailAddressId"
								className="max-w-60"
								selectClassName="dark:bg-gray-700/70"
								value={addressIdToSelectValue(
									instance.defaultEmailAddressId,
									addresses.map((address) => address.id),
								)}
								onChange={(value) =>
									void save({ defaultEmailAddressId: resolveAddressId(value) })
								}
								options={[
									{ label: "Workspace default", value: WORKSPACE_DEFAULT_TEAM },
									...addresses.map((address) => ({
										label: address.primaryEmail,
										value: address.id,
									})),
								]}
							/>
						}
					/>
				) : null}
			</SettingsContainer>

			<PortalContentCard portalInstanceId={instance.id} />

			<SettingsSection title="Manage" className="mb-3" />
			<SettingsContainer className="divide-y-0">
				<SettingItem
					title="Delete portal"
					description="Its docs and changelog entries move back to the main portal."
					action={
						<Button
							variant="outline"
							Icon={Trash2}
							onClick={() => setConfirmDelete(true)}
						>
							Delete
						</Button>
					}
				/>
			</SettingsContainer>

			<AlertDialog
				isOpen={confirmDelete}
				onOpenChange={setConfirmDelete}
				title={`Delete ${instance.name}?`}
				description="Changelogs and docs assigned to this portal move back to the main portal. Issues and projects lose this portal from their assignments."
				confirmText="Delete portal"
				cancelText="Keep portal"
				variant="negative"
				onCancel={() => setConfirmDelete(false)}
				onConfirm={() => {
					setConfirmDelete(false)
					void handleDelete()
				}}
			/>
		</>
	)
}

function SectionSwitch({
	title,
	description,
	checked,
	onChange,
}: {
	title: string
	description: string
	checked: boolean
	onChange: (checked: boolean) => void
}) {
	return (
		<SettingItem
			title={title}
			description={description}
			action={
				<Switch
					name={title}
					label={null}
					className="pr-1"
					checked={checked}
					onCheckedChange={onChange}
				/>
			}
		/>
	)
}
